import { userAtom } from "@/atoms/userAtom";
import { AuthenticationError } from "@/common/AuthenticationError";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  GENERATION_OPTIONS,
  GENDER_OPTIONS,
  PROFILE_PREFECTURE_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  UserGeneration,
  UserGender,
  UserPrefecture,
  UserReferralSource
} from "@/constants/profileOptions";
import { useDemoSender, useDemoFetcherWithSwr } from "@/api";
import { isAuthenticated } from "@/utils/authErrors";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle } from "lucide-react";
import { useForm, Controller } from "react-hook-form";

type ProfileFormData = {
  user_generation: UserGeneration | "";
  user_gender: UserGender | "";
  user_prefecture: UserPrefecture | "";
  user_referral_source: UserReferralSource | "";
  user_preferences: string;
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);

  // React Hook Formの設定
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useForm<ProfileFormData>({
    mode: "onChange",
    defaultValues: {
      user_generation: "",
      user_gender: "",
      user_prefecture: "",
      user_referral_source: "",
      user_preferences: ""
    }
  });

  // プロフィールデータの取得
  const {
    data: profileData,
    isLoading: isProfileLoading,
    mutate: mutateProfile
  } = useDemoFetcherWithSwr({
    apiName: "user_config",
    serviceName: "profile",
    operationId: "getProfile",
    requestParameters: {
      userId: user?.userId || ""
    },
    enabled: !!user?.userId,
    swrConfiguration: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  });

  // 既存データがあれば初期値として設定
  useEffect(() => {
    if (profileData && !isProfileLoading) {
      reset({
        user_generation: (profileData.user_generation as UserGeneration) || "",
        user_gender: (profileData.user_gender as UserGender) || "",
        user_prefecture: (profileData.user_prefecture as UserPrefecture) || "",
        user_referral_source: (profileData.user_referral_source as UserReferralSource) || "",
        user_preferences: profileData.user_preferences || ""
      });
    }
  }, [profileData, isProfileLoading, reset]);

  const {
    sender: updateProfile,
    isLoading,
    error
  } = useDemoSender({
    apiName: "user_config",
    serviceName: "profile",
    operationId: "updateProfile"
  });

  const onSubmit = async (data: ProfileFormData) => {
    if (!user?.userId) {
      return;
    }

    // TypeScriptの型絞り込み: 空文字列を除外してUserGeneration等の型として扱えるようにする
    // formState.isValidでバリデーション済みだが、TypeScriptの型システムは認識できないため明示的にチェック
    if (
      !data.user_generation ||
      !data.user_gender ||
      !data.user_prefecture ||
      !data.user_referral_source
    ) {
      return;
    }

    try {
      await updateProfile({
        userId: user.userId,
        requestBody: {
          user_generation: data.user_generation,
          user_gender: data.user_gender,
          user_prefecture: data.user_prefecture,
          user_referral_source: data.user_referral_source,
          user_preferences: data.user_preferences || undefined
        }
      });

      // プロフィールキャッシュを再検証
      await mutateProfile();

      toast.success("プロフィールを保存しました");
      navigate("/onboarding/line");
    } catch (err) {
      console.error("Failed to update profile:", err);
      toast.error("プロフィールの保存に失敗しました");
    }
  };

  // 認証チェック
  if (!isAuthenticated(user)) {
    return (
      <>
        <Helmet>
          <title>プロフィール入力 | refloop</title>
        </Helmet>

        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <AuthenticationError
            title="ログインが必要です"
            message="プロフィール入力機能をご利用いただくには、アカウントにログインしてください。"
            icon={<UserCircle className="h-12 w-12" />}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>プロフィール入力 | refloop</title>
      </Helmet>

      <div className="flex min-h-screen flex-col bg-white px-4 py-8">
        <div className="mx-auto w-full max-w-md">
          {/* ロゴ */}
          <div className="mb-6 text-center">
            <img src="/logo-type.svg" alt="refloop" className="mx-auto h-8 opacity-70" />
          </div>

          {/* 説明文 */}
          <p className="mb-8 text-center text-sm text-gray-600">
            ご登録ありがとうございます✨
            <br />
            あなたにぴったりな提案をするために
            <br />
            あなたのことを少し教えてください😊
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* 年代 */}
            <div className="space-y-2">
              <Label htmlFor="user_generation">
                年代 <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="user_generation"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isProfileLoading}
                  >
                    <SelectTrigger id="user_generation">
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENERATION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* 性別 */}
            <div className="space-y-2">
              <Label htmlFor="user_gender">
                性別 <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="user_gender"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isProfileLoading}
                  >
                    <SelectTrigger id="user_gender">
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* 都道府県 */}
            <div className="space-y-2">
              <Label htmlFor="user_prefecture">
                都道府県 <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="user_prefecture"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isProfileLoading}
                  >
                    <SelectTrigger id="user_prefecture">
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFILE_PREFECTURE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* refloopを知ったきっかけ */}
            <div className="space-y-2">
              <Label htmlFor="user_referral_source">
                refloopを知ったきっかけ <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="user_referral_source"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isProfileLoading}
                  >
                    <SelectTrigger id="user_referral_source">
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      {REFERRAL_SOURCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* 好きなジャンル（任意） */}
            <div className="space-y-2">
              <Label htmlFor="user_preferences">好きなジャンル（任意）</Label>
              <Controller
                name="user_preferences"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="user_preferences"
                    type="text"
                    placeholder="例: カフェ、ラーメン"
                    disabled={isProfileLoading}
                  />
                )}
              />
            </div>

            {/* エラーメッセージ */}
            {error && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
                プロフィールの保存に失敗しました。もう一度お試しください。
              </div>
            )}

            {/* 保存ボタン */}
            <Button
              type="submit"
              className="w-full"
              disabled={!isValid || isLoading || isProfileLoading}
            >
              {isProfileLoading ? "読み込み中..." : isLoading ? "保存中..." : "保存"}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};
